import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { Body1, Heading6 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { RichIcon } from '@ui/components/RichIcon'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { CheckIcon } from '@ui/icons/Check.icon'

import { isGoogleAuthenticatorType } from '@model/TwoFactorAuthentication'

import { useScrollToTop } from '@hooks/useScrollToTop'

import { TwoFactorAuthForm } from '../../../../TwoFactorAuth/TwoFactorAuthForm'
import {
  TwoFactorAuthLocationState,
  useTwoFactorAuth,
} from '../../../../TwoFactorAuth/useTwoFactorAuth'
import { useTwoFactorAuthGoBackProps } from '../../../../TwoFactorAuth/useTwoFactorAuthGoBackProps'
import { useTwoFactorAuthText } from '../../../../TwoFactorAuth/useTwoFactorAuthText'

import styles from './TwoFactorAuthConfirmPage.module.scss'

export const TwoFactorAuthConfirmPage: React.FC = () => {
  useScrollToTop()

  const navigate = useNavigate()
  const {
    twoFactor,
    resendCode,
    handleVerifyCode,
    error,
    status,
    disableResend,
    initialCountdown,
    resendError,
    openRecoveryCodeModal,
    handleTimer,
    stateChecker,
  } = useTwoFactorAuth()
  const { resendText, errorText } = useTwoFactorAuthText({
    twoFactor,
    initialCountdown,
    resendCode,
    disableResend,
    resendError,
  })

  const { t } = useTranslation()
  const location = useLocation()
  const locationState = location.state as TwoFactorAuthLocationState
  const hasTwoFactorMethods = (locationState?.twoFactorAuthData?.twoFactor?.length ?? 0) > 1
  const twoFactorMethods = locationState?.twoFactorAuthData?.twoFactor || []

  useEffect(() => {
    if (!locationState?.twoFactorAuthData?.twoFactor) {
      navigate('/login')
    }
  }, [locationState, navigate])

  const goBackProps = useTwoFactorAuthGoBackProps(
    locationState?.twoFactorAuthData?.twoFactorAuthEvent ?? '',
    locationState?.twoFactorAuthData?.withdrawalData?.withdrawalPayload?.fromWalletId
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => handleTimer().startResetTimer(), [resendError.hideResend])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => handleTimer().startInitialTimer(), [initialCountdown])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => stateChecker().effect(), stateChecker().dependants)

  return (
    <div className={styles.topWrapper}>
      <div className={styles.wrapper}>
        <div className={styles.inner}>
          <Button.Ghost
            iconLeft={ArrowIcon.Left}
            onClick={() =>
              navigate(goBackProps.path, goBackProps.state ? { state: goBackProps.state } : {})
            }
          >
            {goBackProps.text}
          </Button.Ghost>
          <div className={styles.title}>
            <Heading6 medium>{t('TwoFactorAuth.Confirm it’s you')}</Heading6>
            {status === 'success' && (
              <RichIcon
                bg='surface-secondary-high-em'
                icon={{ icon: CheckIcon, color: 'text-white' }}
                shape='circle'
                size='x24'
              />
            )}
          </div>
          <div>
            <Body1>
              {isGoogleAuthenticatorType(twoFactorMethods?.[0]?.provider?.id)
                ? t('TwoFactorAuth.Enter the verification code generated by your')
                : t('TwoFactorAuth.Enter the verification code sent to')}
            </Body1>
            <br />
            {hasTwoFactorMethods ? (
              <Body1>
                {twoFactorMethods.map((method, idx, array) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && idx === array.length - 1 ? (
                      <Body1> {t('and')} </Body1>
                    ) : idx > 0 ? (
                      ', '
                    ) : (
                      ' '
                    )}
                    <Body1>{method.authMedium}</Body1>
                  </React.Fragment>
                ))}
              </Body1>
            ) : (
              <Body1>{twoFactorMethods[0]?.authMedium ?? ''}</Body1>
            )}
          </div>

          <TwoFactorAuthForm
            resendText={resendText}
            errorText={errorText}
            openRecoveryModal={openRecoveryCodeModal}
            statusType={status}
            onSubmit={handleVerifyCode}
            validationError={error}
          />
        </div>
      </div>
    </div>
  )
}
